import React from 'react';
import { EdgeProps, getSmoothStepPath, getStraightPath, EdgeLabelRenderer } from 'reactflow';
import { EdgeLabelEditor } from '../EdgeLabels/EdgeLabelEditor';
import { FLOW_COLORS } from '../constants';

interface CustomEdgeData {
  label?: string;
  edgeLabel?: string;
  onLabelChange?: (edgeId: string, newLabel: string) => void;
  onLabelDelete?: (edgeId: string) => void;
  onDuplicate?: (edgeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  onDeletePath?: (nodeId: string) => void;
  nodeData?: any;
  parentNodeData?: any;
  isElseBranch?: boolean;
  isEditingMode?: boolean; // New prop to pass editing state
}

export const CustomEdge: React.FC<EdgeProps<CustomEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}) => {
  // Check if this is an end node connection (target ends with '-end')
  const isEndNodeConnection = id.includes('-to-end');

  // Check if this is a Then→End connection that should use branched rendering
  const isThenEndConnection = id.includes('-then-to-end');
  const distance = Math.abs(targetX - sourceX);
  const shouldUseBranchedThenEnd = isThenEndConnection && distance > 50; // If End node is positioned far from handle (indicating branching when Else exists)

  // Get the edge path based on connection type
  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (shouldUseBranchedThenEnd) {
    // Create branched path for Then→End using smooth step path to match Else connector style
    // This will create curved corners and proper routing like other condition branches
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetPosition,
      targetX,
      targetY,
      borderRadius: 16,
    });
  } else if (isEndNodeConnection) {
    // Use straight path for other end node connections
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  } else {
    // Use smooth step path for all other connections
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetPosition,
      targetX,
      targetY,
      borderRadius: 16,
    });
  }

  // Use custom label if available, otherwise use data label (no default fallback)
  const displayLabel = data?.edgeLabel || data?.label;

  const handleLabelSave = (newLabel: string) => {
    if (data?.onLabelChange) {
      data.onLabelChange(id, newLabel);
    }
  };

  const handleLabelDelete = () => {
    if (data?.onLabelDelete) {
      data.onLabelDelete(id);
    }
  };

  const handleDuplicate = () => {
    if (data?.onDuplicate) {
      data.onDuplicate(id);
    }
  };

  const handleDeleteNode = () => {
    if (data?.onDeleteNode && data?.nodeData) {
      data.onDeleteNode(data.nodeData.id);
    }
  };

  const handleDeletePath = () => {
    if (data?.onDeletePath && data?.nodeData) {
      data.onDeletePath(data.nodeData.id);
    }
  };

  // Determine edge color based on type
  const getEdgeStyle = () => {
    const baseStyle = {
      ...style,
      stroke: style?.stroke || FLOW_COLORS.EDGE.DEFAULT,
      strokeWidth: 1.5,
    };

    if (data?.label === 'True') {
      baseStyle.stroke = FLOW_COLORS.EDGE.TRUE_BRANCH;
    } else if (data?.label === 'False') {
      baseStyle.stroke = FLOW_COLORS.EDGE.FALSE_BRANCH;
    }

    return baseStyle;
  };

  return (
    <>
      <path
        id={id}
        style={getEdgeStyle()}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <EdgeLabelEditor
            label={displayLabel}
            onSave={data?.onLabelChange ? handleLabelSave : undefined}
            onDelete={data?.onLabelDelete ? handleLabelDelete : undefined}
            onDuplicate={data?.onDuplicate ? handleDuplicate : undefined}
            onDeleteNode={data?.onDeleteNode ? handleDeleteNode : undefined}
            onDeletePath={data?.onDeletePath ? handleDeletePath : undefined}
            isEditingMode={data?.isEditingMode}
          />
        </div>
      </EdgeLabelRenderer>
    </>
  );
};
